import addUserNote from '../actions/addUserNote';
import removeUserNote from '../actions/removeUserNote';
import { POPUP_GAPSPACE } from '../utils/constants';
import getDirectionalHint from '../utils/getDirectionalHint';
import getUserNoteContent from '../utils/getUserNoteContent';
import { observer } from 'mobx-react-lite';
import { ActionButton, DefaultButton, PrimaryButton } from 'office-ui-fabric-react/lib/Button';
import { Callout } from 'office-ui-fabric-react/lib/Callout';
import { ITextField, TextField } from 'office-ui-fabric-react/lib/components/TextField';
import { Icon } from 'office-ui-fabric-react/lib/Icon';
import { IPoint } from 'office-ui-fabric-react/lib/Utilities';
import { ControlIcons } from 'owa-control-icons';
import loc from 'owa-localize';
import { ClientItem } from 'owa-mail-store';
import React from 'react';
import * as ReactDOM from 'react-dom';
import {
    UserNote_defaultPlaceholderText,
    UserNote_delete,
    UserNote_private,
    UserNote_save,
} from './UserNotePopup.locstring.json';
const styles = require('./UserNotePopup.scss');

interface UserNotePopupProps {
    target: IPoint | HTMLElement;
    selectedText: string;
    element: HTMLElement;
    item: ClientItem;
    isExistingUserNote: boolean;
    selectionInstance: number;
}

let noteDiv: HTMLDivElement = null;

const UserNotePopup = observer(function UserNotePopup(props: UserNotePopupProps) {
    const textField = React.useRef<ITextField>();

    const renderNoteFooter = (): JSX.Element => {
        return (
            <div className={styles.footerContainer}>
                {renderSaveButton()}
                {renderDeleteButton()}
            </div>
        );
    };

    const renderSaveButton = (): JSX.Element => {
        return (
            <PrimaryButton
                text={loc(UserNote_save)}
                className={styles.saveButtonStyles}
                onClick={onSaveNote}
            />
        );
    };

    const renderDeleteButton = (): JSX.Element => {
        return (
            <DefaultButton
                text={loc(UserNote_delete)}
                className={styles.deleteButtonStyles}
                onClick={onDeleteNote}
            />
        );
    };

    const renderNoteHeader = (): JSX.Element => {
        return (
            <div className={styles.topOfNoteContainer}>
                {renderPrivateIndicator()}
                {renderCancelButton()}
            </div>
        );
    };

    const renderCancelButton = (): JSX.Element => {
        return (
            <ActionButton
                className={styles.userNoteButtonStyles}
                iconProps={{
                    iconName: 'Cancel',
                    styles: { root: styles.userNoteButtonIconStyles },
                }}
                onClick={onDismissUserNote}
            />
        );
    };

    function renderPrivateIndicator(): JSX.Element {
        return (
            <div className={styles.outterIconContainer}>
                <Icon className={styles.iconContainer} iconName={ControlIcons.Lock} />
                {loc(UserNote_private)}
            </div>
        );
    }

    const renderTextArea = (): JSX.Element => {
        const { isExistingUserNote, selectedText, selectionInstance, item } = props;

        const noteValueProps = isExistingUserNote
            ? {
                  defaultValue: getUserNoteContent(selectedText, selectionInstance, item),
              }
            : { placeholder: loc(UserNote_defaultPlaceholderText) };

        return (
            <TextField
                componentRef={textField}
                className={styles.userNoteTextArea}
                {...noteValueProps}
                autoFocus={true}
                multiline={true}
                rows={3}
                resizable={false}
                borderless={true}
            />
        );
    };

    const onSaveNote = () => {
        const { element, item, selectedText, selectionInstance, isExistingUserNote } = props;

        const content = getUserNoteContent(selectedText, selectionInstance, item);

        const textInput = textField.current ? textField.current.value : '';

        // Don't add the user note if the input is the same as when it was initially opened
        if (textInput != '' && textInput != content) {
            addUserNote(
                element,
                item,
                selectedText,
                textInput,
                selectionInstance,
                isExistingUserNote
            );
        }

        destroyNotePopupDiv();
    };

    const onDeleteNote = () => {
        const { element, item, selectedText, selectionInstance } = props;

        const textInput = textField.current ? textField.current.value : '';
        removeUserNote(element, item, selectedText, selectionInstance, textInput);

        destroyNotePopupDiv();
    };

    function onDismissUserNote() {
        destroyNotePopupDiv();
    }

    const { target, selectedText } = props;

    return (
        <Callout
            gapSpace={POPUP_GAPSPACE}
            className={styles.userNoteCalloutStyles}
            isBeakVisible={false}
            directionalHint={getDirectionalHint(selectedText)}
            target={target}
            onDismiss={onDismissUserNote}>
            {renderNoteHeader()}
            {renderTextArea()}
            {renderNoteFooter()}
        </Callout>
    );
});

function createNotePopupDiv() {
    noteDiv = document.createElement('div');
    document.body.appendChild(noteDiv);
}

function destroyNotePopupDiv() {
    if (noteDiv) {
        ReactDOM.unmountComponentAtNode(noteDiv);
        noteDiv.parentNode.removeChild(noteDiv);
        noteDiv = null;
    }
}

export default function showUserNote(
    target: IPoint | HTMLElement,
    selectedText: string,
    element: HTMLElement,
    item: ClientItem,
    isExistingUserNote: boolean,
    selectionInstance: number
) {
    if (!noteDiv) {
        createNotePopupDiv();
    }

    ReactDOM.render(
        <React.StrictMode>
            <UserNotePopup
                target={target}
                selectedText={selectedText}
                element={element}
                item={item}
                isExistingUserNote={isExistingUserNote}
                selectionInstance={selectionInstance}
            />
        </React.StrictMode>,
        noteDiv
    );
}
